import { useAuth0 } from '@auth0/auth0-react';
import { API_URL } from '../config/api';
import React from 'react';
import { isPrerendering } from '../utils/detectPrerender';

// Module-level in-memory cache for stale-while-revalidate
// Key: "${url}:${isAuthenticated}" → { data, timestamp }
const _apiCache = new Map();
const STALE_THRESHOLD_MS = 60_000; // 60s before background refetch

// Dedup in-flight background refetches
const _inflightRefetch = new Set();

// Clears cache on auth state transitions detected at render time
let _lastAuthState = null;

function _invalidateCacheIfAuthChanged(isAuthenticated) {
  if (_lastAuthState !== null && _lastAuthState !== isAuthenticated) {
    _apiCache.clear();
  }
  _lastAuthState = isAuthenticated;
}

/**
 * Coerce a list endpoint's payload into the array its consumers assume.
 *
 * `callApi` resolves to null whenever `isPrerendering()` holds -- there is no
 * backend to talk to during react-snap's crawl -- and a component that puts null
 * into array-typed state throws on its next render. One uncaught pageerror sets
 * react-snap's `shuttingDown` flag, which abandons every route still queued, so
 * a single unguarded assignment cost 10 unrelated pages their prerendered HTML.
 *
 * null is expected here and stays silent. Anything else non-array is a real
 * defect -- these endpoints return a bare JSON array -- and would otherwise
 * render as an empty slate indistinguishable from a quiet day, so it is logged.
 *
 * Returns a COPY, never the caller's array. `callApi` keeps the parsed array in
 * `_apiCache` and hands the same reference back for the life of the entry, so a
 * consumer that sorts in place -- useMarketData does, on the very next line --
 * would reorder the cache's own copy for every other reader of that URL.
 *
 * This is a shared normaliser, not the boundary. `callApi` is, and a consumer
 * that forgets to call this is one render away from the same crash; ErrorBoundary
 * now contains the blast radius, and having `callApi` throw a prerender sentinel
 * for the existing catch blocks to swallow would close it properly, at ~33 call
 * sites. Tracked separately.
 */
export const asRows = (data, label) => {
  if (Array.isArray(data)) return [...data];
  if (data != null) {
    // Reached only outside prerender, where null is expected and silent, so this
    // means the endpoint returned something that is not the bare JSON array its
    // contract promises. The UI degrades to an empty slate either way.
    console.warn(`${label}: expected an array, got ${typeof data}. Rendering empty.`, data);
  }
  return [];
};

export const useApi = () => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Invalidate cache when auth state changes (login/logout)
  _invalidateCacheIfAuthChanged(isAuthenticated);

  const callApi = React.useCallback(async (endpoint, options = {}) => {
    // Skip API calls during pre-rendering to prevent hanging
    if (isPrerendering()) {
      return null;
    }

    try {
      // Clean up endpoint to prevent double 'api'
      const cleanEndpoint = endpoint.replace(/^\/?(api\/)?/, '');

      // If API_URL ends with a slash, remove it
      const baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const url = `${baseUrl}/api/${cleanEndpoint}`;

      // Never cache mutations (POST, PUT, DELETE, PATCH)
      if (options.method && options.method !== 'GET') {
        return await _doFetch(url, options, isAuthenticated, getAccessTokenSilently);
      }

      // Cache key includes auth state to prevent tier bleed
      const cacheKey = `${url}:${isAuthenticated}`;
      const cached = _apiCache.get(cacheKey);
      const now = Date.now();
      const isStale = cached && (now - cached.timestamp) > STALE_THRESHOLD_MS;

      // Return cached data immediately if fresh
      if (cached && !isStale) {
        return cached.data;
      }

      // If stale but exists, return cached and refetch in background
      if (cached && isStale) {
        _fetchAndCache(url, cacheKey, options, isAuthenticated, getAccessTokenSilently);
        return cached.data;
      }

      // No cache — fetch synchronously
      const data = await _doFetch(url, options, isAuthenticated, getAccessTokenSilently);
      if (data !== null) {
        _apiCache.set(cacheKey, { data, timestamp: Date.now() });
      }
      return data;
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  return { callApi };
};

// Background refetch — fire-and-forget, updates cache silently
async function _fetchAndCache(url, cacheKey, options, isAuthenticated, getAccessTokenSilently) {
  if (_inflightRefetch.has(cacheKey)) return;
  _inflightRefetch.add(cacheKey);
  try {
    const data = await _doFetch(url, options, isAuthenticated, getAccessTokenSilently);
    if (data !== null) {
      _apiCache.set(cacheKey, { data, timestamp: Date.now() });
    }
  } catch (e) {
    // Background refetch failed — stale cache remains, next call will retry
  } finally {
    _inflightRefetch.delete(cacheKey);
  }
}

// Core fetch logic extracted from original callApi
async function _doFetch(url, options, isAuthenticated, getAccessTokenSilently) {
  let headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  if (isAuthenticated) {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        }
      });

      headers = {
        ...headers,
        'Authorization': `Bearer ${token}`
      };
    } catch (tokenError) {
      console.error('Failed to get access token:', tokenError);
      const error = new Error(tokenError.message || 'Failed to get access token');
      error.isTokenError = true;
      throw error;
    }
  }

  const fetchOptions = { ...options, headers };
  if (fetchOptions.body && typeof fetchOptions.body === 'object') {
    fetchOptions.body = JSON.stringify(fetchOptions.body);
  }

  let response = await fetch(url, fetchOptions);

  // If 401 and authenticated, try refreshing token and retry once
  if (response.status === 401 && isAuthenticated) {
    try {
      const freshToken = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_AUTH0_AUDIENCE
        },
        cacheMode: 'off'
      });

      headers = {
        ...headers,
        'Authorization': `Bearer ${freshToken}`
      };

      response = await fetch(url, {
        ...fetchOptions,
        headers
      });
    } catch (refreshError) {
      console.error('Token refresh failed:', refreshError);
    }
  }

  if (!response.ok) {
    let errorMessage = `HTTP error! status: ${response.status}`;
    let errorCode;
    try {
      const errorData = await response.json();
      errorCode = errorData.code;
      if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
    } catch (e) {
      // If JSON parsing fails, use default message
    }
    const error = new Error(errorMessage);
    error.status = response.status;
    // The server's machine-readable reason (e.g. 'sold_out'), when it sends one.
    error.code = errorCode;
    throw error;
  }

  return await response.json();
}
